import React, { useState, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { hooks } from '../hooks';
import { text } from '../text';
import { theme } from '../constants';
import { components } from '../components';
import axiosInstance from '../services/axiosInstance';

const statusColors: Record<string, { bg: string; color: string }> = {
  draft: { bg: '#F3F4F6', color: '#6B7280' },
  ready: { bg: '#D1FAE5', color: '#065F46' },
};

const typeColors: Record<string, { bg: string; color: string }> = {
  persiapan:  { bg: '#EFF6FF', color: '#1D4ED8' },
  pemeriksaan: { bg: '#FFF7ED', color: '#C2410C' },
};

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });

export const PreCheckReports: React.FC = () => {
  const navigate = hooks.useAppNavigate();
  const { t } = useLanguage();
  const tr = (t as any).preCheckReport || {};

  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/checker-reports');
      setReports(res.data);
    } catch (e) {
      console.error('Failed to fetch pre-check reports:', e);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  React.useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleDelete = async (id: number) => {
    if (!window.confirm(tr.deleteConfirm || 'Hapus laporan ini?')) return;
    try {
      await axiosInstance.delete(`/checker-reports/${id}`);
      setReports(prev => prev.filter(r => r.id !== id));
    } catch (e: any) {
      alert(e?.response?.data?.message || (tr.deleteFailed || 'Gagal menghapus laporan'));
    }
  };

  const renderHeader = () => (
    <components.Header
      title={tr.title || 'Pre-Check Reports'}
      goBack={true}
      plus={true}
      onPlus={() => navigate('/PreCheckReportForm', { state: { mode: 'create' } })}
    />
  );

  const renderContent = () => (
    <div className='container' style={{ paddingTop: 80, paddingBottom: 40, paddingLeft: 16, paddingRight: 16 }}>
      {loading && !initialized ? (
        <div style={{ textAlign: 'center', paddingTop: 60 }}>
          <text.T14 style={{ color: '#9CA3AF' }}>{tr.loading || 'Memuat...'}</text.T14>
        </div>
      ) : reports.length === 0 ? (
        <div style={{ textAlign: 'center', paddingTop: 60 }}>
          <text.T14 style={{ color: '#9CA3AF' }}>{tr.empty || 'Belum ada laporan pre-check.'}</text.T14>
          <div style={{ marginTop: 16 }}>
            <components.Button
              title={tr.createFirst || 'Buat Laporan Pertama'}
              onClick={() => navigate('/PreCheckReportForm', { state: { mode: 'create' } })}
            />
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {reports.map(report => {
            const sColor = statusColors[report.status] ?? statusColors.draft;
            const tColor = typeColors[report.type] ?? { bg: '#F3F4F6', color: '#374151' };
            return (
              <div
                key={report.id}
                style={{
                  backgroundColor: '#FFFFFF', borderRadius: 12,
                  border: '1px solid #E5E7EB', padding: '14px 16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}
              >
                {/* Header row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: 11, fontWeight: '700', padding: '3px 8px', borderRadius: 99,
                      backgroundColor: tColor.bg, color: tColor.color,
                    }}>
                      {report.type === 'persiapan' ? (tr.typePreparation || 'Persiapan') : (tr.typeInspection || 'Pemeriksaan')}
                    </span>
                    <span style={{
                      fontSize: 11, fontWeight: '700', padding: '3px 8px', borderRadius: 99,
                      backgroundColor: sColor.bg, color: sColor.color,
                    }}>
                      {report.status === 'ready' ? (tr.statusReady || 'Siap') : (tr.statusDraft || 'Draft')}
                    </span>
                  </div>
                  <text.T14 style={{ color: '#9CA3AF', fontSize: 12 }}>{fmtDate(report.report_date)}</text.T14>
                </div>

                {/* Vessel */}
                {report.vessel_name && (
                  <text.T14 style={{ color: '#4B5563', marginBottom: 4 }}>
                    🚢 {report.vessel_name}
                  </text.T14>
                )}

                {/* Items count */}
                <text.T14 style={{ color: '#6B7280', fontSize: 12, marginBottom: report.task_description ? 6 : 0 }}>
                  {report.items_count ?? 0} {tr.items || 'item checklist'}
                </text.T14>

                {/* Linked task */}
                {report.task_description && (
                  <div style={{
                    marginTop: 6, padding: '6px 10px', backgroundColor: '#F0FDF4',
                    borderRadius: 8, border: '1px solid #BBF7D0',
                  }}>
                    <text.T14 style={{ color: '#15803D', fontSize: 12 }}>
                      🔗 {tr.linkedTask || 'Dilampirkan ke'}: {report.task_description}
                    </text.T14>
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <components.Button
                    title={tr.btnView || 'Lihat / Edit'}
                    style={{ backgroundColor: '#F3F4F6', color: theme.colors.mainDark, padding: '8px 12px', fontSize: 13 }}
                    containerStyle={{ flex: 1 }}
                    onClick={() => navigate('/PreCheckReportForm', { state: { mode: 'edit', reportId: report.id } })}
                  />
                  {!report.task_description && (
                    <components.Button
                      title={tr.btnDelete || 'Hapus'}
                      style={{ backgroundColor: '#FEE2E2', color: '#DC2626', padding: '8px 12px', fontSize: 13 }}
                      onClick={() => handleDelete(report.id)}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className='screen' style={{ backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
      {renderHeader()}
      {renderContent()}
    </div>
  );
};
